<script setup>
import {onBeforeMount, onMounted, reactive} from 'vue'
import {clone, State, getModels} from '@/paks/vu-app'
import Json5 from 'json5'
import {compressJson5} from '@/paks/js-polyfill'
import Expression from '@/paks/js-expression'

const props = defineProps({
    board: Object,
    parent: Object,
    modelValue: Object,
    widget: Object,
})
const emit = defineEmits(['close', 'remove', 'update:modelValue'])

const {Action} = getModels()

const page = reactive({
    action: null,
    actions: [],
    canDelete: false,
    clouds: [],
    dashboards: State.cache.dashboards.map((d) => d.name).sort(),
    expressionErrors: [],
    conditionErrors: [],
    hasCloud: null,
})

defineExpose({page})

const Types = [
    {title: 'None', value: null, label: 'None', hint: 'No action'},
    {title: 'Change Dashboard', value: 'dashboard', label: 'Dashboard', hint: 'Dashboard name'},
    {title: 'UI Page', value: 'link', label: 'URL Path', hint: '/url'},
    {title: 'Automation Action', value: 'action', label: 'Action Name', hint: 'Action name'},
]

onBeforeMount(() => {
    let action = page.action = clone(props.modelValue)
    action.conditions = action.conditions || [{expression: 'true', params: '{}'}] 
    if (!action.type) {
        action.type = 'action' 
    }
    let clouds = State.find('Cloud').filter((c) => c.type != 'host')
    page.clouds = clouds
    page.hasCloud = clouds.filter((c) => c.id != State.config.evalCloud).length > 0
    page.canDelete = action.type != 'none'
})

onMounted(async () => {
    page.actions = await getActions()
})

function getTypes() {
    let types
    let type = props.widget.type
    if (type == 'form') {
        types = Types.filter((t) => t.value == 'action')
        page.action.type = types[0].value
    } else if (type == 'input') {
        types = Types.slice(0)
    } else {
        types = Types.filter((t) => t.value != 'dashboard')
    }
    if (!page.hasCloud) {
        types = types.filter((t) => t.value != 'action')
    }
    if (State.config.builder) {
        types = types.filter(t => t.value != 'action')
    }
    return types
}

async function getActions() {
    let cloudId = props.widget.cloudId || State.cache.clouds[0]?.id
    if (cloudId && !(cloudId == 'builder' && State.config.builder)) {
        let actions = await Action.find({cloudId})
        return actions.map((t) => t.name)
    }
    return []
}

function getType() {
    return Types.find((t) => t.value == page.action.type)
}

function changeType() {
    let action = page.action
    action.target = null
    action.conditions = []
}

function addCondition(arg, cond) {
    let action = page.action
    if (cond) {
        try {
            if (cond.params) {
                Json5.parse(cond.params)
                page.conditionErrors = []
            }
            try {
                //  Verify the conditions will parse
                let expression = new Expression()
                expression.parse(cond.expression || true)
                page.expressionErrors = []
            } catch (err) {
                page.expressionErrors = ['Cannot parse expression']
                return
            }
        } catch (err) {
            page.conditionErrors = ['Cannot parse condition']
            return
        }
    }
    if (arg == 'new-else') {
        action.conditions.push({params: '{ }'})
    } else if (arg == 'new-elseif') {
        action.conditions.push({expression: ' ', params: '{}'})
    }
}

function removeCondition(arg, index) {
    page.action.conditions.splice(index, 1)
}

function save() {
    try {
        for (let cond of page.action.conditions) {
            if (cond.params) {
                Json5.parse(cond.params)
                cond.params = compressJson5(cond.params)
            }
        }
    } catch (err) {
        throw new Error('Cannot parse condition parameters')
    }
    emit('update:modelValue', page.action)
    emit('close')
}

function deleteAction() {
    emit('remove', page.action)
    emit('close')
}

function cancel() {
    emit('close')
}
</script>

<template>
    <vu-form
        class="action-edit"
        :data="page"
        :save="save"
        :options="{enter: false}"
        help="/doc/ui/action/edit.html"
        :title="`${page.add ? 'Add' : 'Modify'} Action`">
        <vu-input
            v-model="page.action.type"
            label="Action Type"
            type="select"
            :items="getTypes()"
            @update:modelValue="changeType" />

        <vu-input
            v-if="page.action.type == 'dashboard'"
            v-model="page.action.target"
            item-value="name"
            item-title="name"
            label="Dashboards"
            type="select"
            :items="page.dashboards" />

        <vu-input
            v-else-if="page.action.type == 'action'"
            v-model="page.action.target"
            type="combo"
            :label="getType()?.label"
            :items="page.actions"
            :placeholder="getType()?.hint" />

        <vu-input
            v-else-if="page.action.type"
            v-model="page.action.target"
            type="text"
            :label="getType()?.label"
            :placeholder="getType()?.hint" />

        <vu-input
            v-if="page.action.type == 'action' && page.clouds.length > 1"
            v-model="widget.cloudId"
            item-value="id"
            item-title="name"
            label="Cloud"
            type="select"
            :items="page.clouds" />

        <div
            class="vrow"
            v-if="page.action.type == 'action'"
            v-for="(cond, i) in page.action.conditions">

            <vu-input
                v-if="cond.expression != null"
                v-model="cond.expression"
                type="text"
                :errors="page.expressionErrors"
                :label="i == 0 ? 'if' : 'elseif'"
                :name="`${i}-if`"
                placeholder="Expression"
                cols="11"
                variant="outlined"
                @update:modelValue="addCondition('if', cond)" />

            <v-icon
                v-if="i > 0 && cond.expression != null"
                icon="$close"
                variant="outlined"
                @click="removeCondition('if', i)">
            </v-icon>

            <vu-input
                v-model="cond.params"
                type="textarea"
                rows="6"
                :label="cond.expression == null ? 'else' : 'then'"
                :name="`${i}-then`"
                :errors="page.conditionErrors"
                cols="4"
                placeholder="{JSON5}"
                variant="outlined"
                @update:modelValue="addCondition('then', cond)" />

            <v-icon
                v-if="cond.expression == null"
                icon="$close"
                variant="outlined"
                @click="removeCondition('else', i)">
            </v-icon>

            <div>
                <v-btn
                    v-if="cond.expression != null"
                    class="else-button"
                    size="small"
                    variant="outlined"
                    @click="addCondition('new-else')">
                    Else
                </v-btn>
                <v-btn
                    v-if="cond.expression != null"
                    class="else-button"
                    size="small"
                    variant="outlined"
                    @click="addCondition('new-elseif')">
                    Else If
                </v-btn>
            </div>
        </div>
        <div class="actions">
            <v-btn size="small"
                :disabled="!page.action.target"
                color="primary"
                type="submit"
                :loading="page.saving">
                Save
            </v-btn>
            <v-btn size="small" color="none" @click="cancel">Cancel</v-btn>
            <v-btn size="small" color="error" v-if="page.action.type && page.canDelete" @click="deleteAction">
                Delete
            </v-btn>
        </div>
    </vu-form>
</template>

<style lang="scss">
.action-edit {
    padding: 0 10px 10px 10px !important;
    .v-textarea {
        textarea {
            padding-left: 16px !important;
        }
    }
    .else-button {
        border: 1px solid rgb(var(--v-theme-border-lighten-1)) !important;
        margin-bottom: 6px;
        width: 80px;
        display: block;
    }
}
</style>
